// src/lib/sso-return.ts
//
// Where to put a person back after single sign-on has taken them away and returned them to `/login`.
//
// A page address is not a secret, so `sessionStorage` is fine: it dies with the tab, is never sent anywhere, and every read and write
// is wrapped because storage can be blocked, full or absent (a private window, a locked-down browser) and a sign-in must not fail
// for want of a place to remember a path.
//
// ⚠ **An open redirect is refused here, not at the call site.** The stored value is only ever honoured if it is a same-origin path:
// it must start with a single `/`, and `//host` and `/\host` (which a browser reads as another site) are rejected, as is `/login`
// itself, which would loop.
const KEY = 'prs2.returnTo'

function safe(path: string | null | undefined): string | undefined {
  if (path === null || path === undefined) return undefined
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return undefined
  if (path === '/login' || path.startsWith('/login?') || path === '/signed-out') return undefined
  return path
}

export function rememberReturnTo(path: string | undefined): void {
  try {
    const ok = safe(path)
    if (ok === undefined) sessionStorage.removeItem(KEY)
    else sessionStorage.setItem(KEY, ok)
  } catch {
    /* nowhere to remember it: the person lands on the dashboard instead */
  }
}

/** Read without removing, so a render can call it twice (React StrictMode does) and get the same answer. */
export function peekReturnTo(): string | undefined {
  try {
    return safe(sessionStorage.getItem(KEY))
  } catch {
    return undefined
  }
}

export function forgetReturnTo(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    /* nothing to forget */
  }
}
